import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import {
  canViewLocalOpsConcierge,
  LOCALOPS_CONCIERGE_ROUTE,
} from '../../utils/localOpsOpsAccess';
import './LocalOpsConcierge.css';

const USE_CASE_OPTIONS = [
  { value: 'generic_assistance_task', label: 'Generic assistance' },
  { value: 'restaurant_delivery', label: 'Restaurant delivery' },
  { value: 'pickup_errand', label: 'Pickup / errand' },
  { value: 'yard_cleanup', label: 'Yard cleanup' },
  { value: 'ride_help', label: 'Ride help' },
];

const SOURCE_OPTIONS = [
  { value: 'manual_concierge', label: 'Manual concierge' },
  { value: 'facebook_dm', label: 'Facebook DM' },
  { value: 'hip_camden', label: 'Hip Camden' },
  { value: 'down_home', label: 'Down Home' },
  { value: 'restaurant_referral', label: 'Restaurant referral' },
  { value: 'business_outreach', label: 'Business outreach' },
  { value: 'phone_call', label: 'Phone call' },
];

const USE_CASE_PRESETS = {
  generic_assistance_task: {
    category: 'Errands',
    taskType: 'errand',
    title: 'Local assistance task',
    description: 'Help with a local task.',
    requesterFacingNotes: 'Review the task details before funding.',
  },
  restaurant_delivery: {
    category: 'Delivery',
    taskType: 'delivery',
    title: 'Restaurant delivery',
    description: 'Pickup a food order and deliver it locally.',
    requesterFacingNotes: 'Confirm pickup time before funding.',
  },
  pickup_errand: {
    category: 'Pickup / errand',
    taskType: 'errand',
    title: 'Local pickup',
    description: 'Pick up a local item and deliver it to the requester.',
    requesterFacingNotes: 'Confirm item is ready before funding.',
  },
  yard_cleanup: {
    category: 'Yard / cleanup',
    taskType: 'errand',
    title: 'Yard cleanup help',
    description: 'Help with a local cleanup task.',
    requesterFacingNotes: 'Requester should confirm tools/details before funding.',
  },
  ride_help: {
    category: 'Ride / transport',
    taskType: 'assisted_transport',
    title: 'Local ride help',
    description: 'Help with local transportation.',
    requesterFacingNotes: 'Confirm pickup, destination, and timing before funding.',
  },
};

const SCRIPT_CARDS = [
  {
    title: 'Facebook Comment',
    text:
      "Hey - this is exactly the kind of thing JG LocalOps is built for. If you want, I can help turn this into a paid local task so nearby helpers can see it and accept it. It only takes a couple minutes. I'd just need the best email to send you a prefilled link. You can review it first and only fund it if it looks right.",
  },
  {
    title: 'Facebook DM',
    text:
      "Hey, I saw your post about needing help with [task type]. This should be a good fit for JG LocalOps. We can turn it into a paid local task and notify nearby Camden helpers who are looking to earn. I can make it easy: send me the best email, and I'll send you a prefilled link. You can review the task details, adjust anything, and only fund it if it looks right.",
  },
  {
    title: 'Phone/In-Person',
    text:
      "I can help you set that up as a local paid task. The way it works is simple: I'll send you a prefilled link by email. You open it, review the task, make sure the details and price look right, then fund it if you want to post it. Once it's funded, local helpers can be notified and someone can accept it. What's the best email to send that to?",
  },
  {
    title: 'Restaurant/Customer Delivery',
    text:
      "If someone calls in an order and asks about delivery, you can send them to us. We can help them post a local delivery task so a Camden helper can pick it up and bring it to them. For now, the easiest way is: get their email or have them message us. We'll send them a prefilled link, they review it, fund it if it looks right, and local helpers get notified.",
  },
  {
    title: 'Email Hesitation',
    text:
      "No problem. The email is just how we send you the secure task link so you can review it yourself. You're not agreeing to anything by giving the email. You only move forward if the task looks right to you.",
  },
  {
    title: 'Payment Hesitation',
    text:
      "No. First you review the task details. If it looks right, then you fund the task so local helpers know it's real and can accept it. If it doesn't look right, you don't have to post it.",
  },
  {
    title: 'Who Does The Work',
    text:
      'Local people who use JG LocalOps to earn money by completing tasks. Once the task is funded, nearby helpers can be notified and someone can accept it through the platform.',
  },
  {
    title: 'DoorDash Comparison',
    text:
      "Not exactly. It's broader and more local. It can be delivery, pickup, errands, ride help, yard help, small jobs - whatever local task someone needs done. The idea is Camden people helping Camden people.",
  },
  {
    title: 'Nobody Accepts',
    text:
      "You're not stuck. We can share it again, notify helpers again, or raise the pay if needed. The goal is to help the task clear locally.",
  },
  {
    title: 'Safety/Scam',
    text:
      'The task is handled through the platform. Helpers should not ask you to send money privately, buy gift cards, or pay outside the task flow. You review and fund the task yourself, and the helper is paid after the work is completed and verified.',
  },
  {
    title: 'Short Close',
    text: "Send me the best email and I'll get the prefilled task link over to you.",
  },
  {
    title: 'Soft Close',
    text: 'I can send the link over and you can decide after you see it. What email should I use?',
  },
];

const TOOLTIP_COPY = {
  requesterEmail:
    'Used to send the secure review link. The requester is not committed until they review and fund the task.',
  source:
    'Where this need came from. Helps track what channels create funded tasks.',
  sourceLabel:
    'Short label like Hip Camden DM, Down Home call, or phone call.',
  useCase:
    'Drives safe draft defaults for category, task type, task copy, and suggested next scripts.',
  suggestedPriceUsd:
    'The requester can review before funding. Helpers are notified only after the task is funded.',
  requesterFacingNotes:
    'Shown to the requester to help them review the task. Do not put private operator notes here.',
  internalNotes:
    'Only for the concierge/operator. Never sent to requester, helper, public task pages, or share pages.',
  originalRequest:
    'Operator-only capture of the source post, message, phone note, or restaurant note. It is not submitted unless you copy it into task fields.',
};

const OPTIONAL_LABEL = 'Optional';

const defaultForm = () => ({
  requesterEmail: '',
  requesterName: '',
  requesterPhone: '',
  source: 'manual_concierge',
  src: 'manual_concierge',
  sourceLabel: '',
  useCase: 'generic_assistance_task',
  category: USE_CASE_PRESETS.generic_assistance_task.category,
  taskType: USE_CASE_PRESETS.generic_assistance_task.taskType,
  title: '',
  description: '',
  pickupAddress: '',
  deliveryAddress: '',
  suggestedPriceUsd: '',
  requesterFacingNotes: '',
  internalNotes: '',
  originalRequest: '',
});

const normalizeRows = (payload) => {
  const data = payload?.data?.data || payload?.data || payload || [];
  return Array.isArray(data) ? data : [];
};

const trim = (value) => String(value || '').trim();

const getUseCasePreset = (useCase) =>
  USE_CASE_PRESETS[useCase] || USE_CASE_PRESETS.generic_assistance_task;

const getPriceSuggestion = (form) => {
  const hasPickup = Boolean(trim(form.pickupAddress));
  const hasDestination = Boolean(trim(form.deliveryAddress));
  if (!hasPickup || !hasDestination) {
    if (['restaurant_delivery', 'pickup_errand', 'ride_help'].includes(form.useCase)) {
      return {
        value: '',
        label: 'Add pickup/start and destination to unlock a safer operator price suggestion.',
      };
    }
    if (form.useCase === 'yard_cleanup') {
      return {
        value: '',
        label: 'Yard cleanup pricing should stay operator-entered.',
      };
    }
    return {
      value: '',
      label: 'Enter the operator estimate the requester should review before funding.',
    };
  }
  if (form.useCase === 'restaurant_delivery') {
    return {
      value: '14',
      label: 'Local delivery minimum. Adjust upward if distance, timing, or order complexity requires it.',
    };
  }
  if (form.useCase === 'pickup_errand') {
    return {
      value: '12',
      label: 'Local pickup minimum. Adjust upward after distance and wait time are known.',
    };
  }
  if (form.useCase === 'ride_help') {
    return {
      value: '8',
      label: 'Ride-help floor. Use the $2/mi mental model once the real distance is known.',
    };
  }
  return {
    value: '',
    label: 'No automatic price suggestion for this use case.',
  };
};

const buildDraftPayload = (form) => ({
  requesterEmail: form.requesterEmail,
  requesterName: form.requesterName,
  requesterPhone: form.requesterPhone,
  source: form.source,
  src: form.src,
  sourceLabel: form.sourceLabel,
  useCase: form.useCase,
  category: form.category,
  taskType: form.taskType,
  title: form.title,
  description: form.description,
  pickupAddress: form.pickupAddress,
  deliveryAddress: form.deliveryAddress,
  suggestedPriceUsd: Number(form.suggestedPriceUsd),
  requesterFacingNotes: form.requesterFacingNotes,
  internalNotes: form.internalNotes,
  regionSlug: 'camden',
  marketSlug: 'camden',
  intent: 'post_task',
});

const FieldLabel = ({
  children,
  optional = false,
  tooltip,
}) => (
  <span className="localops-concierge__field-label">
    <span>{children}</span>
    <span className={optional ? 'localops-concierge__optional' : 'localops-concierge__required'}>
      {optional ? OPTIONAL_LABEL : 'Required'}
    </span>
    {tooltip ? (
      <span
        className="localops-concierge__tip"
        tabIndex="0"
        title={tooltip}
        aria-label="Field info"
      >
        ?
      </span>
    ) : null}
  </span>
);

export default function LocalOpsConcierge() {
  const { player } = useContext(AuthContext);
  const allowed = canViewLocalOpsConcierge(player);
  const [form, setForm] = useState(defaultForm);
  const [manualFields, setManualFields] = useState(() => new Set());
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copiedScript, setCopiedScript] = useState('');
  const [lastLinkSent, setLastLinkSent] = useState(false);

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${player?.token || ''}`,
      },
    }),
    [player?.token],
  );

  const fetchDrafts = useCallback(async () => {
    if (!allowed) return;
    setLoadingDrafts(true);
    try {
      const res = await axiosConn.get(
        'local-ops/concierge/assistance-drafts?limit=25',
        authConfig,
      );
      setDrafts(normalizeRows(res));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load concierge drafts.',
      );
    } finally {
      setLoadingDrafts(false);
    }
  }, [allowed, authConfig]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const updateField = (field, value, { markManual = true } = {}) => {
    const nextManual = new Set(manualFields);
    if (markManual) nextManual.add(field);
    setManualFields(nextManual);
    setForm((current) => {
      let next = {
        ...current,
        [field]: value,
        ...(field === 'source' ? { src: value } : {}),
      };
      if (field === 'useCase') {
        const preset = getUseCasePreset(value);
        next = {
          ...next,
          taskType: preset.taskType,
        };
        ['category', 'title', 'description', 'requesterFacingNotes'].forEach((presetField) => {
          if (!nextManual.has(presetField)) {
            next[presetField] = preset[presetField] || '';
          }
        });
      }
      if (['useCase', 'pickupAddress', 'deliveryAddress'].includes(field)) {
        const suggestion = getPriceSuggestion(next);
        if (!nextManual.has('suggestedPriceUsd')) {
          next.suggestedPriceUsd = suggestion.value || '';
        }
      }
      return next;
    });
    setError('');
    setNotice('');
    setLastLinkSent(false);
  };

  const copyOriginalRequestTo = (field) => {
    const sourceText = trim(form.originalRequest);
    if (!sourceText) return;
    updateField(field, sourceText);
  };

  const handleCopy = async (card) => {
    try {
      await navigator.clipboard?.writeText(card.text);
      setCopiedScript(card.title);
    } catch (err) {
      setCopiedScript('');
      setError('Copy failed. Select and copy the script manually.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!allowed) return;
    setSubmitting(true);
    setError('');
    setNotice('');
    try {
      const createRes = await axiosConn.post(
        'local-ops/concierge/assistance-drafts',
        buildDraftPayload(form),
        authConfig,
      );
      const created = createRes?.data?.data || {};
      const draftId = created.draftId;
      if (!draftId) throw new Error('Draft was created without an id.');

      const sendRes = await axiosConn.post(
        `local-ops/concierge/assistance-drafts/${draftId}/send-magic-link`,
        {},
        authConfig,
      );
      const sent = sendRes?.data?.data || {};
      setNotice(
        `Magic link sent. ${sent.fallbackReviewLink || created.fallbackReviewLink || ''}`.trim(),
      );
      setLastLinkSent(true);
      setForm(defaultForm());
      setManualFields(new Set());
      await fetchDrafts();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to create and send this draft.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const priceSuggestion = getPriceSuggestion(form);
  const needCaptured = Boolean(trim(form.originalRequest) || trim(form.description) || trim(form.title));
  const emailCaptured = Boolean(trim(form.requesterEmail));
  const draftClear = Boolean(trim(form.title) && trim(form.description));
  const priceSuggested = Number(form.suggestedPriceUsd) > 0;
  const relevantScriptTitles = new Set();
  if (!emailCaptured) {
    relevantScriptTitles.add('Short Close');
    relevantScriptTitles.add('Soft Close');
    relevantScriptTitles.add('Email Hesitation');
  }
  if (form.source === 'facebook_dm') {
    relevantScriptTitles.add('Facebook Comment');
    relevantScriptTitles.add('Facebook DM');
  }
  if (form.useCase === 'restaurant_delivery') {
    relevantScriptTitles.add('Restaurant/Customer Delivery');
  }
  if (!priceSuggested) relevantScriptTitles.add('Payment Hesitation');
  if (/scam|safe|safety|gift card|outside/i.test(form.originalRequest)) {
    relevantScriptTitles.add('Safety/Scam');
  }

  if (!allowed) {
    return (
      <main className="localops-concierge localops-concierge--locked" data-testid="localops-concierge-unauthorized">
        <section>
          <JGLocalOpsMark className="localops-concierge__brand" />
          <h1>Concierge unavailable</h1>
          <span className="localops-concierge__subtitle">
            LocalOps Concierge access is required.
          </span>
        </section>
      </main>
    );
  }

  return (
    <main className="localops-concierge" data-testid="localops-concierge-workbench">
      <header className="localops-concierge__header">
        <div>
          <JGLocalOpsMark className="localops-concierge__brand" />
          <h1>Camden Concierge</h1>
          <span className="localops-concierge__subtitle">
            Prefill local task drafts, send secure review links, and keep CS-ride separate.
          </span>
        </div>
        <a href="/localops/ops">Ops Health</a>
      </header>

      <section className="localops-concierge__grid">
        <form className="localops-concierge__panel" onSubmit={handleSubmit}>
          <div className="localops-concierge__section-title">
            <h2>Assistance Draft</h2>
            <span>{form.useCase}</span>
          </div>

          <div className="localops-concierge__checklist" data-testid="concierge-conversion-checklist">
            {[
              ['Need captured', needCaptured],
              ['Email captured', emailCaptured],
              ['Draft clear', draftClear],
              ['Price suggested', priceSuggested],
              ['Link sent', lastLinkSent],
              ['Awaiting claim/funding', lastLinkSent],
            ].map(([label, complete]) => (
              <span
                key={label}
                className={complete ? 'is-complete' : ''}
              >
                {complete ? '[x]' : '[ ]'} {label}
              </span>
            ))}
          </div>

          <label>
            <FieldLabel tooltip={TOOLTIP_COPY.requesterEmail}>
              Requester email
            </FieldLabel>
            <input
              type="email"
              value={form.requesterEmail}
              onChange={(event) => updateField('requesterEmail', event.target.value)}
              required
            />
          </label>

          <div className="localops-concierge__two">
            <label>
              <FieldLabel optional>Requester name</FieldLabel>
              <input
                value={form.requesterName}
                onChange={(event) => updateField('requesterName', event.target.value)}
              />
            </label>
            <label>
              <FieldLabel optional>Phone</FieldLabel>
              <input
                value={form.requesterPhone}
                onChange={(event) => updateField('requesterPhone', event.target.value)}
              />
            </label>
          </div>

          <div className="localops-concierge__two">
            <label>
              <FieldLabel tooltip={TOOLTIP_COPY.source}>
                Source
              </FieldLabel>
              <select
                data-testid="concierge-source-select"
                value={form.source}
                onChange={(event) => updateField('source', event.target.value)}
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <FieldLabel tooltip={TOOLTIP_COPY.useCase}>
                Use case
              </FieldLabel>
              <select
                data-testid="concierge-use-case-select"
                value={form.useCase}
                onChange={(event) => updateField('useCase', event.target.value)}
              >
                {USE_CASE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label>
            <FieldLabel optional tooltip={TOOLTIP_COPY.sourceLabel}>
              Source label
            </FieldLabel>
            <input
              value={form.sourceLabel}
              onChange={(event) => updateField('sourceLabel', event.target.value)}
              placeholder="Hip Camden DM, Down Home call, phone call"
            />
          </label>

          <label>
            <FieldLabel optional tooltip={TOOLTIP_COPY.originalRequest}>
              Original request / source post
            </FieldLabel>
            <textarea
              data-testid="concierge-original-request"
              value={form.originalRequest}
              onChange={(event) => updateField('originalRequest', event.target.value)}
              placeholder="Paste the Facebook post, copied message, phone note, or restaurant/customer note here."
            />
          </label>
          <div className="localops-concierge__copy-actions">
            <button
              type="button"
              onClick={() => copyOriginalRequestTo('description')}
              disabled={!trim(form.originalRequest)}
            >
              Copy to description
            </button>
            <button
              type="button"
              onClick={() => copyOriginalRequestTo('internalNotes')}
              disabled={!trim(form.originalRequest)}
            >
              Copy to internal notes
            </button>
            <button
              type="button"
              onClick={() => copyOriginalRequestTo('requesterFacingNotes')}
              disabled={!trim(form.originalRequest)}
            >
              Copy to requester-facing notes
            </button>
          </div>

          <div className="localops-concierge__two">
            <label>
              <FieldLabel optional>Category</FieldLabel>
              <input
                value={form.category}
                onChange={(event) => updateField('category', event.target.value)}
              />
            </label>
            <label>
              <FieldLabel tooltip={TOOLTIP_COPY.suggestedPriceUsd}>
                Suggested price
              </FieldLabel>
              <input
                type="number"
                min="1"
                step="0.01"
                value={form.suggestedPriceUsd}
                onChange={(event) => updateField('suggestedPriceUsd', event.target.value)}
                required
              />
            </label>
          </div>
          <p className="localops-concierge__hint" data-testid="concierge-price-hint">
            {priceSuggestion.label}
          </p>

          <label>
            <FieldLabel>Title</FieldLabel>
            <input
              value={form.title}
              onChange={(event) => updateField('title', event.target.value)}
              required
            />
          </label>

          <label>
            <FieldLabel>Description</FieldLabel>
            <textarea
              value={form.description}
              onChange={(event) => updateField('description', event.target.value)}
              required
            />
          </label>

          <div className="localops-concierge__two">
            <label>
              <FieldLabel optional>Pickup / start</FieldLabel>
              <input
                value={form.pickupAddress}
                onChange={(event) => updateField('pickupAddress', event.target.value)}
              />
            </label>
            <label>
              <FieldLabel optional>Delivery / destination</FieldLabel>
              <input
                value={form.deliveryAddress}
                onChange={(event) => updateField('deliveryAddress', event.target.value)}
              />
            </label>
          </div>

          <label>
            <FieldLabel optional tooltip={TOOLTIP_COPY.requesterFacingNotes}>
              Requester-facing notes
            </FieldLabel>
            <textarea
              value={form.requesterFacingNotes}
              onChange={(event) => updateField('requesterFacingNotes', event.target.value)}
            />
          </label>

          <label>
            <FieldLabel optional tooltip={TOOLTIP_COPY.internalNotes}>
              Internal notes
            </FieldLabel>
            <textarea
              value={form.internalNotes}
              onChange={(event) => updateField('internalNotes', event.target.value)}
              placeholder="Never sent to requester/helper/public surfaces."
            />
          </label>

          <section className="localops-concierge__preview" data-testid="concierge-task-preview">
            <div>
              <h3>What the requester will review</h3>
              <dl>
                <dt>Title</dt>
                <dd>{trim(form.title) || 'Not set yet'}</dd>
                <dt>Description</dt>
                <dd>{trim(form.description) || 'Not set yet'}</dd>
                <dt>Pickup / start</dt>
                <dd>{trim(form.pickupAddress) || 'Optional / not set'}</dd>
                <dt>Delivery / destination</dt>
                <dd>{trim(form.deliveryAddress) || 'Optional / not set'}</dd>
                <dt>Suggested price</dt>
                <dd>{priceSuggested ? `$${Number(form.suggestedPriceUsd).toFixed(2)}` : 'Not set yet'}</dd>
                <dt>Requester-facing notes</dt>
                <dd>{trim(form.requesterFacingNotes) || 'None'}</dd>
              </dl>
            </div>
            <div className="localops-concierge__preview-internal">
              <h3>Internal only</h3>
              <dl>
                <dt>Source</dt>
                <dd>{form.source}</dd>
                <dt>Source label</dt>
                <dd>{trim(form.sourceLabel) || 'Optional / not set'}</dd>
                <dt>Internal notes</dt>
                <dd>{trim(form.internalNotes) || 'None'}</dd>
                <dt>Original request / source post</dt>
                <dd>{trim(form.originalRequest) || 'Not captured'}</dd>
              </dl>
            </div>
          </section>

          {error ? <div className="localops-concierge__error">{error}</div> : null}
          {notice ? <div className="localops-concierge__notice">{notice}</div> : null}

          <button type="submit" disabled={submitting}>
            {submitting ? 'Sending...' : 'Create draft & send magic link'}
          </button>
        </form>

        <section className="localops-concierge__panel" data-testid="concierge-assist-panel">
          <div className="localops-concierge__section-title">
            <h2>Concierge Assist</h2>
            <span>static scripts</span>
          </div>
          <p className="localops-concierge__core-line">
            I can send you a prefilled link. You can review it first, adjust anything,
            and only fund it if it looks right.
          </p>
          <div className="localops-concierge__scripts">
            {SCRIPT_CARDS.map((card) => (
              <article
                key={card.title}
                className={`localops-concierge__script-card ${
                  relevantScriptTitles.has(card.title) ? 'is-relevant' : ''
                }`.trim()}
                data-relevant={relevantScriptTitles.has(card.title) ? 'true' : 'false'}
              >
                <div>
                  <strong>
                    {card.title}
                    {relevantScriptTitles.has(card.title) ? (
                      <span className="localops-concierge__script-badge">Suggested</span>
                    ) : null}
                  </strong>
                  <button type="button" onClick={() => handleCopy(card)}>
                    {copiedScript === card.title ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </section>
      </section>

      <section className="localops-concierge__panel localops-concierge__drafts">
        <div className="localops-concierge__section-title">
          <h2>Recent Drafts</h2>
          <span>{loadingDrafts ? 'loading' : `${drafts.length} rows`}</span>
        </div>
        {drafts.length ? (
          <div className="localops-concierge__draft-list">
            {drafts.map((draft) => (
              <article key={draft.id || draft._id}>
                <strong>{draft.title}</strong>
                <span>{draft.status}</span>
                <p>
                  {draft.useCase || 'generic_assistance_task'} / {draft.sourceLabel || draft.source}
                </p>
                <small>{draft.customerEmail}</small>
              </article>
            ))}
          </div>
        ) : (
          <p>No generic concierge drafts yet.</p>
        )}
      </section>
    </main>
  );
}

export { SCRIPT_CARDS, USE_CASE_OPTIONS, SOURCE_OPTIONS, LOCALOPS_CONCIERGE_ROUTE };
